import type {
  BattleState,
  CombatEvent,
  CombatUnit,
  ShipClass,
  GameState,
} from "../core/types";
import { seedHash } from "../core/rng";

export const BATTLE_BACKGROUNDS = [
  "01-azure-veil",
  "02-ember-rift",
  "03-violet-tide",
  "04-emerald-wake",
  "05-silver-reach",
  "06-roseglass-expanse",
  "07-amber-shoals",
  "08-cobalt-trench",
  "09-jade-aurora",
  "10-obsidian-bloom",
] as const;

/** Scenery cycles through player engagements without sampling the simulation RNG. */
export function battleBackground(g: GameState) {
  const completed = g.reports.filter(
    (r) => r.attacker === g.player || r.defender === g.player,
  ).length;
  // After the 200-report history window fills, use the saved engagement seed so
  // scenery keeps varying rather than getting stuck on a capped report count.
  const sequence =
    g.reports.length < 200 ? completed : (g.battle?.seed ?? g.nextId);
  return `backgrounds/${BATTLE_BACKGROUNDS[(seedHash(g.seed) + sequence) % BATTLE_BACKGROUNDS.length]}.webp`;
}

/** Sprite centre and all its bars fit within the shared weapons plane. */
export function battleSpriteHeight(planeHeight: number, count: number) {
  const rows = Math.max(1, Math.min(4, count));
  const gap = rows > 1 ? (planeHeight * 410) / 600 / (rows - 1) : Infinity;
  return Math.max(
    16,
    Math.floor(
      Math.min(104, planeHeight * 0.23, planeHeight * 0.4 - 80, gap - 44),
    ),
  );
}

/** A filled hit disk includes the planet, with a 48 px minimum diameter where space allows. */
export function planetHitRadius(scale: number, nearestDistance = Infinity) {
  return Math.max(
    26,
    Math.min(24 / Math.max(0.05, scale), nearestDistance * 0.46),
  );
}

export interface Position {
  x: number;
  y: number;
}
export interface Shot {
  event: CombatEvent;
  from: Position;
  to: Position;
  source: CombatUnit;
  delay: number;
  impact: number;
  destroys: boolean;
}
export function flagship(ships: { type: ShipClass }[]): ShipClass {
  return ships.some((u) => u.type === "dreadnought")
    ? "dreadnought"
    : ships.some((u) => u.type === "cruiser")
      ? "cruiser"
      : "scout";
}
/** Coordinates share the same 1000 × 600 plane as the SVG weapons layer. */
export function formation(
  count: number,
  index: number,
  enemy: boolean,
): Position {
  const rows = Math.min(4, count),
    column = Math.floor(index / rows);
  const x = count > 4 ? (column ? 330 : 145) : 235;
  const y =
    count === 1 ? 280 : 70 + (index % rows) * (410 / Math.max(1, rows - 1));
  return { x: enemy ? 1000 - x : x, y };
}
export function positions(b: BattleState, playerSide: "attacker" | "defender") {
  const out = new Map<string, Position>();
  for (const side of ["attacker", "defender"] as const)
    b[side].units.forEach((u, i) =>
      out.set(u.id, formation(b[side].units.length, i, side !== playerSide)),
    );
  return out;
}
/** Presentation reads completed combat events; it never mutates or samples the simulator. */
export function shotsFor(
  b: BattleState,
  events: CombatEvent[],
  playerSide: "attacker" | "defender",
  speed: number,
): Shot[] {
  const at = positions(b, playerSide),
    units = [...b.attacker.units, ...b.defender.units];
  return events
    .filter((e) => e.type === "hit" || e.type === "miss")
    .flatMap((event, i) => {
      const from = at.get(event.source),
        target = at.get(event.target),
        source = units.find((u) => u.id === event.source);
      if (!from || !target || !source) return [];
      return [
        {
          event,
          from,
          source,
          to: {
            x: target.x,
            y: target.y + (event.type === "miss" ? (i % 2 ? 50 : -50) : 0),
          },
          delay: Math.round((i * 110) / speed),
          impact: Math.round((i * 110 + 200) / speed),
          destroys:
            event.type === "hit" &&
            events.some(
              (e) =>
                e.type === "destroy" &&
                e.target === event.target &&
                e.source === event.source &&
                e.round === event.round,
            ),
        },
      ];
    });
}
